// Oversized image import guard. Fork-only.
//
// Stops images over IMAGE_IMPORT_MAX_BYTES before they reach the scene and
// routes the insert through a compress dialog. One hook covers paste, drop and
// the toolbar insert: upstream calls the `generateIdForFile` prop first in
// `initializeImage` and skips its own resize and 4 MiB check when the returned
// id already exists in its files map, so a substituted compressed file needs no
// packages/ change.

import { useCallback, useEffect, useRef, useState } from "react";

import {
  generateIdFromFile,
  getDataURL,
} from "@excalidraw/excalidraw/data/blob";

import type {
  BinaryFileData,
  DataURL,
  ExcalidrawImperativeAPI,
} from "@excalidraw/excalidraw/types";
import type { FileId } from "@excalidraw/element/types";

import {
  compressImageFile,
  type CompressImageOptions,
  type CompressImageResult,
} from "./compressImage";
import { COMPRESSION_FORMATS, IMAGE_IMPORT_MAX_BYTES } from "./config";

// Fixed sentinel, not a translated string. Upstream catches the cancellation
// and shows `error.message` in its own error dialog, so a normal cancel would
// look like a failure. The hook watches `onChange` and clears exactly this
// value. It must never compare against translated text: the user can switch
// language between the throw and the render, and a translated comparison would
// silently stop matching.
export const IMAGE_IMPORT_CANCEL_SENTINEL = "drawola:image-import-cancelled";

const INITIAL_FORMAT = COMPRESSION_FORMATS[0];

export type ImageImportRecompressAction = "otherFormat" | "smaller";

export type ImageImportState =
  | { phase: "idle" }
  | { phase: "tooLarge"; fileName: string; sizeBytes: number }
  | { phase: "working" }
  | { phase: "preview"; fileName: string; result: CompressImageResult }
  | { phase: "couldNotFit"; fileName: string; result: CompressImageResult };

export type ImageImportCompressFn = (
  file: File,
  options: CompressImageOptions,
) => Promise<CompressImageResult | null>;

export type ImageImportGuard = {
  generateIdForFile: (file: File) => Promise<string>;
  state: ImageImportState;
  accept: () => Promise<void>;
  cancel: () => void;
  recompress: (action: ImageImportRecompressAction) => Promise<void>;
};

type AcceptedOutcome = {
  fileId: FileId;
  dataURL: DataURL;
  mimeType: string;
};

type RememberedDecision =
  | ({ kind: "accept" } & AcceptedOutcome)
  | { kind: "cancel" };

type PendingPrompt = {
  file: File;
  resolve: (outcome: AcceptedOutcome) => void;
  reject: (error: Error) => void;
};

export const useImageImportGuard = (
  excalidrawAPI: ExcalidrawImperativeAPI | null,
  compress: ImageImportCompressFn = compressImageFile,
): ImageImportGuard => {
  const [state, setState] = useState<ImageImportState>({ phase: "idle" });

  // The visible dialog phase, mirrored into a ref so the callbacks below can
  // read it without going stale. `state` alone drives rendering.
  const phaseRef = useRef<ImageImportState["phase"]>("idle");

  // Serialises concurrent `generateIdForFile` calls. Dropping several files
  // must open one dialog at a time, not a stack of fighting dialogs.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  const pendingRef = useRef<PendingPrompt | null>(null);
  const resultRef = useRef<CompressImageResult | null>(null);

  // Session-level memory of each oversized file's decision, keyed on the plain
  // file hash, so inserting the same image twice does not ask twice.
  const rememberedRef = useRef<Map<string, RememberedDecision>>(new Map());

  const apiRef = useRef(excalidrawAPI);
  apiRef.current = excalidrawAPI;

  const reset = useCallback(() => {
    pendingRef.current = null;
    resultRef.current = null;
    phaseRef.current = "idle";
    setState({ phase: "idle" });
  }, []);

  const runCompress = useCallback(
    async (file: File, format: string, maxBytes: number) => {
      const prompt = pendingRef.current;
      if (!prompt) {
        return;
      }
      phaseRef.current = "working";
      setState({ phase: "working" });
      const result = await compress(file, { format, maxBytes });
      // Cancelled while compressing: the dialog already closed, keep it closed.
      if (pendingRef.current !== prompt) {
        return;
      }
      if (!result) {
        // Every format failed to encode in this browser. Abort quietly instead
        // of showing a fake error dialog.
        const promptToReject = pendingRef.current;
        reset();
        promptToReject?.reject(new Error(IMAGE_IMPORT_CANCEL_SENTINEL));
        return;
      }
      resultRef.current = result;
      if (result.fits) {
        phaseRef.current = "preview";
        setState({ phase: "preview", fileName: file.name, result });
      } else {
        phaseRef.current = "couldNotFit";
        setState({ phase: "couldNotFit", fileName: file.name, result });
      }
    },
    [compress, reset],
  );

  // Covers two buttons: in "tooLarge" it starts the compression, in "preview"
  // it accepts the shown result and inserts the compressed file.
  const accept = useCallback(async () => {
    const prompt = pendingRef.current;
    if (
      !prompt ||
      (phaseRef.current !== "tooLarge" && phaseRef.current !== "preview")
    ) {
      return;
    }
    if (phaseRef.current === "tooLarge") {
      await runCompress(prompt.file, INITIAL_FORMAT, IMAGE_IMPORT_MAX_BYTES);
      return;
    }
    const result = resultRef.current;
    if (!result) {
      return;
    }
    const fileId = await generateIdFromFile(
      new File([result.file], prompt.file.name, { type: result.format }),
    );
    const dataURL = await getDataURL(result.file);
    const mimeType = result.format as BinaryFileData["mimeType"];
    apiRef.current?.addFiles([
      {
        id: fileId,
        mimeType,
        dataURL,
        created: Date.now(),
        lastRetrieved: Date.now(),
      },
    ]);
    reset();
    prompt.resolve({ fileId, dataURL, mimeType });
  }, [reset, runCompress]);

  const cancel = useCallback(() => {
    const prompt = pendingRef.current;
    if (!prompt) {
      return;
    }
    reset();
    prompt.reject(new Error(IMAGE_IMPORT_CANCEL_SENTINEL));
  }, [reset]);

  const recompress = useCallback(
    async (action: ImageImportRecompressAction) => {
      const prompt = pendingRef.current;
      const result = resultRef.current;
      if (!prompt || !result) {
        return;
      }
      if (action === "otherFormat") {
        const other =
          result.format === "image/webp" ? "image/jpeg" : "image/webp";
        await runCompress(prompt.file, other, IMAGE_IMPORT_MAX_BYTES);
        return;
      }
      // Strictly under the current size, so the compress ladder returns the
      // next smaller rung instead of re-serving the same result.
      await runCompress(
        prompt.file,
        result.format,
        Math.max(1, result.bytes - 1),
      );
    },
    [runCompress],
  );

  const enqueuePrompt = useCallback((file: File) => {
    const task = queueRef.current.then(
      () =>
        new Promise<AcceptedOutcome>((resolve, reject) => {
          pendingRef.current = { file, resolve, reject };
          phaseRef.current = "tooLarge";
          setState({
            phase: "tooLarge",
            fileName: file.name,
            sizeBytes: file.size,
          });
        }),
    );
    // Keep the queue chain alive after a rejection so the next queued file
    // still gets its turn.
    queueRef.current = task.then(
      () => undefined,
      () => undefined,
    );
    return task;
  }, []);

  const generateIdForFile = useCallback(
    async (file: File): Promise<string> => {
      const rawId = await generateIdFromFile(file);
      if (file.size <= IMAGE_IMPORT_MAX_BYTES) {
        return rawId;
      }
      const remembered = rememberedRef.current.get(rawId);
      if (remembered) {
        if (remembered.kind === "cancel") {
          throw new Error(IMAGE_IMPORT_CANCEL_SENTINEL);
        }
        return remembered.fileId;
      }
      let outcome: AcceptedOutcome;
      try {
        outcome = await enqueuePrompt(file);
      } catch (error) {
        rememberedRef.current.set(rawId, { kind: "cancel" });
        throw error;
      }
      rememberedRef.current.set(rawId, { kind: "accept", ...outcome });
      return outcome.fileId;
    },
    [enqueuePrompt],
  );

  // Upstream turns a thrown `generateIdForFile` error into its error dialog.
  // A normal cancel must not look like a failure, so clear the sentinel the
  // moment it reaches appState. Best effort: it can flash for one frame, which
  // is still better than a fake error.
  useEffect(() => {
    if (!excalidrawAPI) {
      return;
    }
    return excalidrawAPI.onChange((_elements, appState) => {
      if (appState.errorMessage === IMAGE_IMPORT_CANCEL_SENTINEL) {
        excalidrawAPI.updateScene({ appState: { errorMessage: null } });
      }
    });
  }, [excalidrawAPI]);

  return { generateIdForFile, state, accept, cancel, recompress };
};
